import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { memo, useState } from 'react';

import { Tooltip } from '../../atoms/Tooltip/Tooltip';

// --- InputForm ---
export const InputForm = memo(
  ({
    input,
    handleInputChange,
    handleFormSubmit,
    isLoading,
    inputRef,
  }: {
    input: string;
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleFormSubmit: (e: React.FormEvent) => void;
    isLoading: boolean;
    inputRef: React.RefObject<HTMLTextAreaElement | null>;
  }) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isComposing, setIsComposing] = useState(false);
    const maxLength = 1000;
    const minLength = 2;
    const remainingChars = maxLength - input.length;

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      const isEnter = e.key === 'Enter';
      const isShift = e.shiftKey;
      const isInvalid =
        isLoading || input.length < minLength || input.length > maxLength;

      // IME入力中のイベントのコード
      const isIMEInput = e.nativeEvent.keyCode === 229;

      if (isComposing || isIMEInput) {
        return;
      }

      if (isEnter && !isShift && !isInvalid) {
        e.preventDefault();
        handleFormSubmit(e);
      }
    };

    const handleComposition = (flag: boolean) => {
      setIsComposing(flag);
    };

    return (
      <Box
        component="form"
        onSubmit={handleFormSubmit}
        display="flex"
        flexDirection="column"
        gap={1}
        position="relative"
      >
        <TextField
          fullWidth
          value={input}
          onChange={handleInputChange}
          placeholder={`AIに質問する...`}
          disabled={isLoading}
          variant="outlined"
          multiline
          minRows={4}
          maxRows={15}
          inputRef={inputRef}
          onKeyDown={handleKeyDown}
          onCompositionStart={() => handleComposition(true)}
          onCompositionEnd={() => handleComposition(false)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          sx={{
            '& .MuiInputBase-root': {
              paddingBottom: '60px', // 下部にスペースを確保
            },
          }}
          slotProps={{
            input: {
              inputRef: inputRef,
              endAdornment: (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    backgroundColor: 'background.paper',
                    zIndex: 1,
                    padding: '4px',
                    borderRadius: 1,
                  }}
                >
                  {/* ストリーミング中は非表示 */}
                  {!isLoading && (
                    <Typography
                      variant="caption"
                      color={
                        remainingChars < 0 || input.length < minLength
                          ? 'error'
                          : 'text.secondary'
                      }
                    >
                      {input.length < minLength
                        ? isFocused
                          ? `${minLength}文字以上入力してください`
                          : ''
                        : `${remainingChars} / ${maxLength}文字`}
                    </Typography>
                  )}

                  <Tooltip
                    title={
                      isLoading ||
                      input.length < minLength ||
                      input.length > maxLength
                        ? ''
                        : 'Enter で送信, Shift + Enterで改行'
                    }
                    arrow
                  >
                    <span>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={
                          isLoading ||
                          input.length < minLength ||
                          input.length > maxLength
                        }
                        size="small"
                      >
                        {isLoading ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          '送信'
                        )}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              ),
            },

            htmlInput: {
              maxLength,
            },
          }}
        />
      </Box>
    );
  }
);
